import { supabase } from './supabase.ts';
import { MaintenanceRecord, MaintenanceFormData } from '../types/maintenance.ts';

export const maintenanceService = {
  async fetchRecords(propertyId: string) {
    const { data, error } = await supabase
      .from('maintenance_records')
      .select('*')
      .eq('property_id', propertyId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Error fetching maintenance records: ${error.message}`);
    }

    return data as MaintenanceRecord[];
  },

  async createRecord(recordData: MaintenanceFormData) {
    const { data, error } = await supabase
      .from('maintenance_records')
      .insert([recordData])
      .select()
      .single();

    if (error) {
      throw new Error(`Error creating maintenance record: ${error.message}`);
    }

    return data as MaintenanceRecord;
  },

  async updateRecord(id: string, recordData: Partial<MaintenanceFormData>) {
    const { data, error } = await supabase
      .from('maintenance_records')
      .update(recordData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Error updating maintenance record: ${error.message}`);
    }

    return data as MaintenanceRecord;
  },

  async deleteRecord(id: string) {
    const { error } = await supabase
      .from('maintenance_records')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(`Error deleting maintenance record: ${error.message}`);
    }
  },

  async getMaintenanceCosts(propertyId: string, timeframe?: 'month' | 'year') {
    let query = supabase
      .from('maintenance_records')
      .select('cost, created_at')
      .eq('property_id', propertyId)
      .neq('status', 'Cancelled');  // Add this line to exclude cancelled records

    if (timeframe) {
      const now = new Date();
      const startDate = timeframe === 'month' 
        ? new Date(now.getFullYear(), now.getMonth(), 1)
        : new Date(now.getFullYear(), 0, 1);

      query = query.gte('created_at', startDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Error fetching maintenance costs: ${error.message}`);
    }

    return data.reduce((sum, record) => sum + (record.cost || 0), 0);
  }
};
